import { HttpApiSchema } from "@effect/platform"
import { Effect, Predicate, Schema } from "effect"

import * as CurrentUser from "./CurrentUser"

export const ErrorBaseSchema = Schema.Struct({
  type: Schema.String,
  title: Schema.String,
  detail: Schema.String,
})

export class NotFound extends Schema.TaggedError<NotFound>()(
  "@superwall/schema/models/errors/NotFound",
  {
    entityType: Schema.String,
    entityId: Schema.NullOr(Schema.Union(Schema.String, Schema.Number)),
  },
  HttpApiSchema.annotations({ status: 404, title: "NotFound" })
) {
  get message() {
    if (this.entityId) {
      return `${this.entityType} with identifier ${this.entityId} not found`
    }
    return `${this.entityType} not found`
  }

  static is(u: unknown): u is NotFound {
    return Predicate.isTagged(u, "@superwall/schema/models/errors/NotFound")
  }
}

export class InternalServerError extends Schema.TaggedError<InternalServerError>()(
  "@superwall/schema/models/errors/InternalServerError",
  {
    ...ErrorBaseSchema.fields,
    cause: Schema.Defect,
    type: Schema.optionalWith(ErrorBaseSchema.fields.type, {
      default: () => "https://superwall.com/errors/internal-server-error",
    }),
  },
  HttpApiSchema.annotations({
    status: 500,
    title: "InternalServerError",
    description: "An unexpected error occurred",
  })
) {
  static is(u: unknown): u is InternalServerError {
    return Predicate.isTagged(u, "@superwall/schema/models/errors/InternalServerError")
  }
}

export class Unauthorized extends Schema.TaggedError<Unauthorized>()(
  "@superwall/schema/models/errors/Unauthorized",
  {
    ...ErrorBaseSchema.fields,
    type: Schema.optionalWith(ErrorBaseSchema.fields.type, {
      default: () => "https://superwall.com/errors/unauthorized",
    }),
  },
  HttpApiSchema.annotations({ status: 401, title: "Unauthorized" })
) {
  static is(u: unknown): u is Unauthorized {
    return Predicate.isTagged(u, "@superwall/schema/models/errors/Unauthorized")
  }

  static refail(entity: string, action: string) {
    return <A, E, R>(
      effect: Effect.Effect<A, E, R>
    ): Effect.Effect<A, Unauthorized, CurrentUser.Context | R> =>
      Effect.catchIf(
        effect,
        (e) => !Unauthorized.is(e),
        () =>
          Effect.flatMap(
            CurrentUser.Context,
            (actor) =>
              new Unauthorized({
                title: `Unauthorized action`,
                detail: `You are not authorized to perform ${action} on ${entity} for ${actor.userId}`,
              })
          )
      ) as any
  }
}

export class BadRequest extends Schema.TaggedError<BadRequest>()(
  "@superwall/schema/models/errors/BadRequest",
  {
    ...ErrorBaseSchema.fields,
    type: Schema.optionalWith(ErrorBaseSchema.fields.type, {
      default: () => "https://superwall.com/errors/bad-request",
    }),
  },
  HttpApiSchema.annotations({
    status: 400,
    title: "BadRequest",
    description: "The request was invalid",
  })
) {
  static is(u: unknown): u is InternalServerError {
    return Predicate.isTagged(u, "@superwall/schema/models/errors/BadRequest")
  }
}

export class ConflictError extends Schema.TaggedError<ConflictError>()(
  "@superwall/schema/models/errors/ConflictError",
  {
    ...ErrorBaseSchema.fields,
    type: Schema.optionalWith(ErrorBaseSchema.fields.type, {
      default: () => "https://superwall.com/errors/Conflict",
    }),
  },
  HttpApiSchema.annotations({ status: 409, title: "ConflictError" })
) {
  static is(u: unknown): u is ConflictError {
    return Predicate.isTagged(u, "@superwall/schema/models/errors/ConflictError")
  }
}

export function withRemapDbErrors<R, E extends { _tag: string }, A>(
  entityType: string,
  action: "update" | "create" | "delete" | "select",
  entityId?: any | { value: any; key: string }[]
) {
  return (
    effect: Effect.Effect<R, E, A>
  ): Effect.Effect<
    R,
    Exclude<E, { _tag: "DatabaseError" | "ParseError" }> | InternalServerError,
    A
  > => {
    return effect.pipe(
      Effect.catchTags({
        DatabaseError: (err: any) =>
          Effect.fail(
            new InternalServerError({
              title: `Error ${action}ing ${entityType}`,
              detail: constructDetailMessage(
                "There was an error in parsing when",
                entityType,
                entityId
              ),
              cause: err,
            })
          ),
        ParseError: (err: any) =>
          Effect.fail(
            new InternalServerError({
              title: `Error ${action}ing ${entityType}`,
              detail: constructDetailMessage(
                "There was an error in parsing when",
                entityType,
                entityId
              ),
              cause: err,
            })
          ),
      })
    )
  }
}

const constructDetailMessage = (
  title: string,
  entityType: string,
  entityId?: any | { value: any; key: string }[]
) => {
  if (entityId) {
    if (Array.isArray(entityId)) {
      return `${title} the ${entityType} with values ${entityId
        .map((value) => value.key + ": " + value.value)
        .join(", ")}`
    }
    return `${title} the ${entityType} with id ${entityId}`
  }

  return `${title} the ${entityType}`
}
